import React, { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { apiService } from '../services/api';
import { UpdateEquipmentDto } from '../types';
import EquipmentForm from '../components/forms/EquipmentForm';
import { ArrowLeft } from 'lucide-react';

const EditEquipmentPage: React.FC = () => {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const equipmentId = parseInt(id!);
	const [isFormDirty, setIsFormDirty] = useState(false);
	const lastNavigateAttemptRef = useRef<string | null>(null);
	const lastPromptAtRef = useRef<number>(0);
	const suppressNavigationRef = useRef<boolean>(false);

	const {
		data: equipmentResponse,
		isLoading: equipmentLoading,
		error: equipmentError
	} = useQuery({
		queryKey: ['equipment', equipmentId],
		queryFn: () => apiService.getEquipmentById(equipmentId),
		enabled: !!equipmentId
	});

	const updateEquipmentMutation = useMutation({
		mutationFn: async ({ id, data }: { id: number; data: UpdateEquipmentDto }) => {
			const result = await apiService.updateEquipment(id, data);
			return result;
		},
		onSuccess: (response) => {
			if (response.success && response.data) {
				queryClient.invalidateQueries({ queryKey: ['equipment'] });
				toast.success(`Equipment "${response.data.name}" updated successfully!`);
				navigate(`/equipment/${equipmentId}`);
			} else {
				toast.error(`Error: ${response.message || 'Unknown error occurred'}`);
			}
		},
		onError: (error: any) => {
			console.error('Error updating equipment:', error);
			const errorMessage = error?.response?.data?.message ||
				error?.response?.data?.errors?.join(', ') ||
				error.message ||
				'Unknown error';
			toast.error(`Error updating equipment: ${errorMessage}`);
		}
	});

	const handleSubmit = (data: UpdateEquipmentDto) => {
		suppressNavigationRef.current = true;
		updateEquipmentMutation.mutate({ id: equipmentId, data });
	};

	const handleBackClick = () => {
		if (!isFormDirty) {
			navigate(`/equipment/${equipmentId}`);
			return;
		}

		const now = Date.now();
		const targetPath = `/equipment/${equipmentId}`;
		const samePath = lastNavigateAttemptRef.current === targetPath;
		const withinWindow = now - lastPromptAtRef.current < 5000;

		if (samePath && withinWindow) {
			// proceed with navigation
			lastNavigateAttemptRef.current = null;
			lastPromptAtRef.current = 0;
			suppressNavigationRef.current = true;
			navigate(targetPath);
			return;
		}

		lastNavigateAttemptRef.current = targetPath;
		lastPromptAtRef.current = now;
		toast(() => (
			<span className="text-sm">
				You have unsaved changes. Click the back button again to leave.
			</span>
		), { id: 'equipment-back-warning', duration: 4000, icon: '⚠️' });
	};

	if (equipmentLoading) {
		return (
			<div className="p-4">
				<div className="animate-pulse">
					<div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
					<div className="h-64 bg-gray-200 dark:bg-gray-700 rounded"></div>
				</div>
			</div>
		);
	}

	if (equipmentError || !equipmentResponse?.data) {
		return (
			<div className="p-4">
				<div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
					<p className="text-red-800 dark:text-red-200">Error loading equipment. Please try again.</p>
				</div>
			</div>
		);
	}

	const equipment = equipmentResponse.data;

	return (
		<div className="p-4 space-y-6">
			{/* Header */}
			<div className="mb-2">
				<div className="flex items-center gap-2 mb-2">
					<span className="text-2xl">{equipment.equipmentTypeIcon}</span>
					<div>
						<h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Edit Equipment</h1>
						<p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
							{equipment.equipmentTypeDisplay} • {equipment.clientLocationName}
						</p>
					</div>
				</div>
				{/* Back to Equipment Link */}
				<button
					onClick={handleBackClick}
					className="flex items-center gap-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
				>
					<ArrowLeft className="w-4 h-4" />
					<span>Back to Equipment</span>
				</button>
			</div>

			<div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
				<EquipmentForm
					equipment={equipmentResponse.data}
					onSubmit={handleSubmit}
					isLoading={updateEquipmentMutation.isPending}
					error={updateEquipmentMutation.error ? 'Failed to update equipment' : undefined}
					onFormDirtyChange={setIsFormDirty}
				/>
			</div>
		</div>
	);
};

export default EditEquipmentPage;

